import { useState, useEffect, useCallback } from 'react';
import {
  load,
  save,
  createThread as createThreadReducer,
  selectThread as selectThreadReducer,
  addMessage as addMessageReducer,
  setThreadMessages as setThreadMessagesReducer,
  deleteThread as deleteThreadReducer,
  renameThread as renameThreadReducer,
  renameThreadFromFirstMessage as renameThreadFromFirstMessageReducer,
  updateThreadPatient as updateThreadPatientReducer,
  getMessagesForThread,
  getVisibleThreads,
  getThreadsByPatient,
  getCurrentThread,
  formatThreadTitle,
  type ThreadState,
  type ThreadItem,
  type MessageWithDate,
  type PatientInfo,
} from '../services/threadService';
import { getStoredSmartWorkspacePatient, isSmartModeActive } from '../api/smartApi';
import {
  mergeServerConversations,
  messagesFromServerTurns,
  setActiveThreadUser,
} from '../services/threadService';
import { fetchConversationTurns, fetchMyConversations, isConversationsApiAvailable } from '../api/conversationsApi';
import { getIamAccessState, subscribeIamAccess } from '../auth/iamAccess';

export interface UseThreadsReturn {
  // State
  threads: ThreadItem[];
  visibleThreads: ThreadItem[];
  threadsByPatient: { patientLabel: string; threads: ThreadItem[] }[];
  currentThreadId: string;
  currentThread: ThreadItem | undefined;
  messages: MessageWithDate[];

  // Actions
  createThread: (opts?: { patientName?: string; patientId?: string; id?: string }) => void;
  selectThread: (threadId: string) => void;
  addMessage: (threadId: string, msg: MessageWithDate) => void;
  setThreadMessages: (threadId: string, messages: MessageWithDate[]) => void;
  deleteThread: (threadId: string) => void;
  renameThread: (threadId: string, title: string) => void;
  renameThreadFromFirstMessage: (threadId: string, firstUserContent: string) => void;
  updateThreadPatient: (threadId: string, patient: PatientInfo) => void;
}

function getSmartPatientContext(): { patientId: string; patientName: string } | null {
  if (!isSmartModeActive()) return null;
  const patient = getStoredSmartWorkspacePatient();
  const patientId = patient?.mrn?.trim() || '';
  if (!patientId) return null;
  return { patientId, patientName: patient?.name?.trim() || 'Current patient' };
}

function scopeStateToSmartPatient(state: ThreadState, smartPatient: { patientId: string; patientName: string } | null): ThreadState {
  if (!smartPatient) return state;
  const threads = state.threads.filter((thread) => thread.patientId === smartPatient.patientId);
  const currentThreadId = threads.some((thread) => thread.id === state.currentThreadId)
    ? state.currentThreadId
    : threads[0]?.id || '';
  return { ...state, threads, currentThreadId };
}

export function useThreads(): UseThreadsReturn {
  const [state, setState] = useState<ThreadState>(() => {
    // Scope persistence to the signed-in clinician BEFORE the first load, so
    // another user's locally cached threads are never even read.
    setActiveThreadUser(getIamAccessState().profile?.user?.id ?? '');
    return load();
  });

  useEffect(() => {
    save(state);
  }, [state]);

  // Re-scope storage when the authenticated user changes (shared workstation
  // sign-out/sign-in) — reload that user's own store, never the previous one's.
  useEffect(() => {
    return subscribeIamAccess((access) => {
      if (setActiveThreadUser(access.profile?.user?.id ?? '')) {
        setState(load());
      }
    });
  }, []);

  // Server-side owner-scoped history: the backend lists ONLY this clinician's
  // conversations (for the launched patient when in SMART mode). Local cache
  // keeps bodies; the server list is what makes history follow the doctor
  // across devices. Best-effort — offline keeps working from local state.
  useEffect(() => {
    if (!isConversationsApiAvailable()) return undefined;
    let cancelled = false;
    const sync = () => {
      const smartPatient = getSmartPatientContext();
      void fetchMyConversations({ patientId: smartPatient?.patientId, limit: 100 })
        .then((conversations) => {
          if (!cancelled && conversations.length > 0) {
            setState((s) => mergeServerConversations(s, conversations));
          }
        })
        .catch(() => {});
    };
    sync();
    window.addEventListener('march-smart-patient-ready', sync);
    return () => {
      cancelled = true;
      window.removeEventListener('march-smart-patient-ready', sync);
    };
  }, []);

  useEffect(() => {
    if (!isSmartModeActive() || typeof window === 'undefined') return undefined;

    const syncSmartPatientThread = () => {
      const patient = getSmartPatientContext();
      if (!patient?.patientId) return;

      setState((current) => {
        const existing = current.threads.find((thread) => thread.patientId === patient.patientId);
        if (existing) {
          const shouldRename = existing.patientName !== patient.patientName || !existing.title.includes(patient.patientName);
          const next = shouldRename
            ? {
                ...current,
                threads: current.threads.map((thread) =>
                  thread.id === existing.id
                    ? { ...thread, patientName: patient.patientName, title: formatThreadTitle(patient.patientName, new Date(thread.timestamp)) }
                    : thread,
                ),
              }
            : current;
          return selectThreadReducer(next, existing.id);
        }

        const threadId = `smart-${patient.patientId}`;
        const created = createThreadReducer(current, {
          id: threadId,
          patientName: patient.patientName,
          patientId: patient.patientId,
        });
        return {
          ...created,
          threads: created.threads.map((thread) =>
            thread.id === threadId
              ? { ...thread, title: formatThreadTitle(patient.patientName, new Date(thread.timestamp)) }
              : thread,
          ),
        };
      });
    };

    syncSmartPatientThread();
    window.addEventListener('march-smart-patient-ready', syncSmartPatientThread);
    window.addEventListener('storage', syncSmartPatientThread);
    return () => {
      window.removeEventListener('march-smart-patient-ready', syncSmartPatientThread);
      window.removeEventListener('storage', syncSmartPatientThread);
    };
  }, []);

  const createThread = useCallback((opts?: { patientName?: string; patientId?: string; id?: string }) => {
    const smartPatient = getSmartPatientContext();
    setState((s) => createThreadReducer(s, smartPatient ? {
      ...opts,
      patientName: smartPatient.patientName,
      patientId: smartPatient.patientId,
    } : opts ?? {}));
  }, []);

  const selectThread = useCallback((threadId: string) => {
    const smartPatient = getSmartPatientContext();
    let shouldRestore = false;
    setState((s) => {
      if (smartPatient) {
        const allowed = s.threads.some((thread) => thread.id === threadId && thread.patientId === smartPatient.patientId);
        if (!allowed) return s;
      }
      shouldRestore = (s.messagesByThread[threadId] ?? []).length === 0;
      return selectThreadReducer(s, threadId);
    });
    // A thread known from the server but never opened on this device has no
    // local bodies — restore the clinician's own turns from the server.
    if (shouldRestore && isConversationsApiAvailable()) {
      void fetchConversationTurns(threadId)
        .then((turns) => {
          if (turns.length === 0) return;
          setState((s) => {
            if ((s.messagesByThread[threadId] ?? []).length > 0) return s;
            return setThreadMessagesReducer(s, threadId, messagesFromServerTurns(turns));
          });
        })
        .catch(() => {});
    }
  }, []);

  const addMessage = useCallback((threadId: string, msg: MessageWithDate) => {
    setState((s) => addMessageReducer(s, threadId, msg));
  }, []);

  const setThreadMessages = useCallback((threadId: string, messages: MessageWithDate[]) => {
    setState((s) => setThreadMessagesReducer(s, threadId, messages));
  }, []);

  const deleteThread = useCallback((threadId: string) => {
    setState((s) => deleteThreadReducer(s, threadId));
  }, []);

  const renameThread = useCallback((threadId: string, title: string) => {
    setState((s) => renameThreadReducer(s, threadId, title));
  }, []);

  const renameThreadFromFirstMessage = useCallback((threadId: string, firstUserContent: string) => {
    setState((s) => renameThreadFromFirstMessageReducer(s, threadId, firstUserContent));
  }, []);

  const updateThreadPatient = useCallback((threadId: string, patient: PatientInfo) => {
    const smartPatient = getSmartPatientContext();
    setState((s) => updateThreadPatientReducer(
      s,
      threadId,
      smartPatient ? { name: smartPatient.patientName, mrn: smartPatient.patientId } : patient,
    ));
  }, []);

  const smartPatient = getSmartPatientContext();
  const viewState = scopeStateToSmartPatient(state, smartPatient);
  const messages = getMessagesForThread(viewState, viewState.currentThreadId);
  const visibleThreads = getVisibleThreads(viewState);
  const threadsByPatient = getThreadsByPatient(viewState);
  const currentThread = getCurrentThread(viewState);

  return {
    threads: viewState.threads,
    visibleThreads,
    threadsByPatient,
    currentThreadId: viewState.currentThreadId,
    currentThread,
    messages,
    createThread,
    selectThread,
    addMessage,
    setThreadMessages,
    deleteThread,
    renameThread,
    renameThreadFromFirstMessage,
    updateThreadPatient,
  };
}
